import { useContext, useEffect, useRef, useState } from "react";
import moment from "moment";
import { connect } from "react-redux";
import "./CheckOutPayment.css";
import { useNavigate, useLocation } from "react-router-dom";
import useQuery from "../../utilities/Query";
import Button from "../../UI/Button/Button";
import CheckoutEditConfirmPopup from "./CheckoutEditConfirmPopup";
import {
  clearCart,
  setEntireCart,
  setOrderDetails,
  setPaymentSuccess,
  setPageCount,
  setOrderInfo
} from "../../state_management/actions";
import * as actions from "../../state_management/actionTypes";
import CheckOutListReschedule from "../CheckOutList/CheckOutListReschedule";
import { CheckOutSuccess } from "../../pages/CheckOut/CheckOut";
import { CheckOutPaymentLeft } from "./CheckOutPaymentLeft";
import usePayment, { parseDirectOrder } from "./usePayment";
import { cancelDirectOrder } from "../../state_management/epics/order";
import { isDepositAvailable } from ".";
import { calc_price_with_discount } from "../../utilities/misc";
import { orderType } from "../../state_management/reducers/order";
import { stateType } from "../../store";
import { initialState as defaultOrderDetails } from "../../state_management/reducers/order";
import { cartInitialState } from "../../state_management/reducers/cart";
import {
  epics_cart_items,
  parseToCartItem
} from "../../utilities/cartUtilities";
import { LocaleContext, TotalWithCouponContext } from "../../AppProvider";
import CheckOutPaymentElements from "./CheckOutPaymentElements";

/**
 *
 * What do I need
 * - Start and end dates
 * - Price at deposit and full payment but without the ability to change
 * - Client secret for making payments
 * - Get the wrapper element to wrap the payment form
 * - A list of the services, discount applied and total
 */

export const total = (coupTotal: number | null | undefined, total: number) => {
  const allowNum = typeof coupTotal === "number";

  if (allowNum) {
    return coupTotal;
  } else {
    return total;
  }
};

const largeButtonContainerId = "checkout_direct_large_button_container";
const smallButtonContainerId = "checkout_direct_small_button_container";
const PaymentDirect = ({
  directPaymentInfo
}: {
  directPaymentInfo: actions.orderDetailsType;
}) => {
  const [finished, setFinished] = useState(
    directPaymentInfo.data.status === "confirmed"
  );

  const customer_email = directPaymentInfo.data.customer.email;

  const reserved =
    directPaymentInfo.data.status === "pending" ||
    directPaymentInfo.data.status === "reserved";

  return reserved && !finished ? (
    <PaymentDirectForm
      setFinished={setFinished}
      directPaymentInfo={directPaymentInfo}
    />
  ) : directPaymentInfo.data.status === "ongoing" ? ( // will only reach here on reload and when the order is ongoin
    <CheckOutOngoing />
  ) : finished || directPaymentInfo.data.status === "confirmed" ? (
    <CheckOutSuccess customer_email={customer_email} /> // will reach here both on when the order is confirmed and when the payment is sent successfully for processing
  ) : (
    <></>
  );
};

export default PaymentDirect;

const PaymentDirectFormCon = ({
  directPaymentInfo,
  setFinished,
  details,
  setOrderDetails,
  clearCart,
  setEntireCart,
  cart
}: {
  directPaymentInfo: actions.orderDetailsType;
  setFinished: (data: boolean) => void;
  details: orderType;
  setOrderDetails: (details: orderType) => void;
  clearCart: (ignoreAnalytics: boolean) => void;
  setEntireCart: (items: cartInitialState) => void;
  cart: cartInitialState;
  setPageCount: (data: number) => void;
  setOrderInfo: (info: any) => void;
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const query = useQuery();
  const { t } = useContext(LocaleContext);
  const isEditing = useRef(false);
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleEdit = () => {
    setShowConfirmModal(true);
  };

  const confirmEdit = () => {
    setShowConfirmModal(false);
    isEditing.current = true;
    const token = query.get("token") || "";
    if (token) {
      cancelDirectOrder(token).catch(console.error);
    }
    setOrderInfo(null);
    setPageCount(0);
    navigate(pathname, { replace: true });
  };

  const [error, setError] = useState<string>();
  const [processing, setProcessing] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const orderDetails = directPaymentInfo.data;
  const isReserved = orderDetails.status === "reserved";
  const clientSecret = orderDetails.client_secret;

  const cartItems = orderDetails.services;
  const req_gift_values = orderDetails.gift_values || [];
  const extras = orderDetails.extra_services || [];

  const { extra_services, gift_values, services } = epics_cart_items({
    cart: cartItems.map((a) => ({
      ...a,
      is_upsell: false
    })),
    extras,
    gift_values: req_gift_values.map((a) => ({
      ...a,
      is_upsell: false,
      item: { price: a.value }
    }))
  });

  const parseCart = {
    extra_services,
    services
  };

  useEffect(() => {
    const details = parseDirectOrder(orderDetails);
    setOrderDetails({ ...details });
  }, [JSON.stringify(orderDetails)]);

  const {
    cart: cart_items,
    extras: extra_items,
    gift_services
  } = parseToCartItem({
    cart: cartItems,
    extras,
    gift_values: req_gift_values
  });
  useEffect(() => {
    setEntireCart({
      cart: cart_items.concat(gift_services),
      extras: extra_items
    });
  }, [JSON.stringify(orderDetails)]);

  const [totalWithCoupon] = useContext(TotalWithCouponContext);

  const usePay = usePayment({
    services: parseCart.services,
    details,
    gift_values: gift_values,
    extra_services: parseCart.extra_services,
    orderInfo: {
      cleaner_id: orderDetails.cleaner_id,
      clientSecret,
      orderId: orderDetails.id,
      status: orderDetails.status,
      random_cleaner: !!orderDetails.random_cleaner,
      time_start: moment(orderDetails.time_start).format("YYYY-MM-DD HH:mm")
    },
    setOrderInfo: () => {},
    isDirect: true,
    //Other
    clientSecret,
    Total: totalWithCoupon,
    setError,
    setDisabled,
    setProcessing,
    setPaymentSuccess,
    startSubmit: () => {
      setFinished(false);
    },
    successOnSubmit: () => {
      setFinished(true);
    },
    cart: cartItems.map((item) => ({
      type: "typical",
      extra: false,
      id: item.id,
      item,
      quantity: item.quantity
    })),
    extras: extras.map((item) => ({
      type: "extra",
      extra: true,
      id: item.id,
      item: {
        ...item,
        price_with_discount: calc_price_with_discount(item.discount, item.price)
      },
      quantity: item.quantity
    }))
  });

  const {
    TotalDeposit,
    createOrUpdateOrderRes,
    depositRes,
    depositValue,
    showContent,
    processPaymentHooks
  } = usePay;

  const loading = createOrUpdateOrderRes.loading;

  return (
    <>
      {showConfirmModal && (
        <CheckoutEditConfirmPopup 
          close={() => setShowConfirmModal(false)} 
          onConfirm={confirmEdit} 
        />
      )}
      <div className="checkOutPayment_wrapper">
      <div className="checkout_form checkout_content_wrapper">
        <div className="checkout_left">
          <div>
            <CheckOutPaymentLeft
              depositUnavailable={isDepositAvailable(depositRes.data)}
              TotalDeposit={TotalDeposit}
              children={
                (clientSecret && showContent() && (
                  <CheckOutPaymentElements
                    returnUrl={window.location.href}
                    clientSecret={clientSecret}
                    customerDetails={{
                      firstName: orderDetails.customer.firstname,
                      lastName: orderDetails.customer.lastname,
                      email: orderDetails.customer.email,
                      phone: orderDetails.customer.mobile,
                      address: orderDetails.customer.address
                    }}
                    error={error}
                    disabledButton={
                      loading ||
                      disabled ||
                      processing ||
                      !clientSecret ||
                      depositValue === undefined
                    }
                    processPaymentHooks={processPaymentHooks}
                    Button={{
                      large: {
                        component: (props) => <PaymentButtons {...props} onEdit={isReserved ? handleEdit : undefined} />,
                        id: largeButtonContainerId
                      },
                      small: {
                        id: smallButtonContainerId,
                        component: (props) => (
                          <PaymentButtons {...props} onEdit={isReserved ? handleEdit : undefined} smallScreen />
                        )
                      }
                    }}
                  />
                )) || <></>
              }
            />
            <SmallButton showDefault={!clientSecret || !showContent()} />
          </div>
        </div>
        <div className="checkout_right">
          <CheckOutListReschedule
            buttonJSX={
              <LargeButton showDefault={!clientSecret || !showContent()} />
            }
            couponCheck
            orderDetails={details}
            time_start={orderDetails.time_start}
            time_end={orderDetails.time_end}
            cartCouponTotal={totalWithCoupon}
            cart={cart_items}
            gift_services={gift_services}
            extras={extra_items}
            coupon={details.coupon_code}
          />
        </div>
      </div>
    </div>
    </>
  );
};

const SmallButton = ({ showDefault }: { showDefault: boolean }) => {
  return (
    <>
      <div id={smallButtonContainerId}></div>
      {showDefault && (
        <PaymentButtons handler={() => {}} disabled={true} smallScreen />
      )}
    </>
  );
};

const LargeButton = ({ showDefault }: { showDefault: boolean }) => {
  return (
    <>
      <div id={largeButtonContainerId}></div>
      {showDefault && <PaymentButtons handler={() => {}} disabled={true} />}
    </>
  );
};

const mapStateToProps = (state: stateType) => ({
  details: state.orderReducer.details,
  cart: state.cartReducer
});

const mapDispatchToProps = (dispatch: any) => ({
  setOrderDetails: (details: orderType) => {
    dispatch(setOrderDetails(details));
  },
  clearCart: (ignoreAnalytics: boolean) => {
    dispatch(clearCart(ignoreAnalytics));
  },
  setEntireCart: (items: cartInitialState) => {
    dispatch(setEntireCart(items));
  },
  setPageCount: (data: number) => {
    dispatch(setPageCount(data));
  },
  setOrderInfo: (info: any) => {
    dispatch(setOrderInfo(info));
  }
});

const PaymentDirectForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(PaymentDirectFormCon);

const PaymentButtons = ({
  smallScreen,
  handler,
  disabled,
  onEdit
}: {
  smallScreen?: boolean;
  handler: () => void;
  disabled?: boolean;
  onEdit?: () => void;
}) => {
  const { t } = useContext(LocaleContext);
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <div className={`${smallScreen ? `showSm` : ``}`}>
      <div className={`check_list_btn_wrapper direct`}>
        <div className="check_list_btn_inner flex" style={{ gap: '10px' }}>
          {onEdit && (
            <Button onClick={onEdit} plane size="sm" span>
              {t("Edit Booking" as any) || "Edit Booking"}
            </Button>
          )}
          <Button onClick={handler} disabled={disabled} size="sm" span>
            {t("Pay")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CheckOutOngoing = () => {
  const { t } = useContext(LocaleContext);
  return (
    <div className=" checkout_left check_out_successful_wrapper center fdCol">
      <div className="top center">
        <span>{t("Payment is processing.")}</span>
      </div>
      <div className="bottom">
        <p className="fontBlue">
          {t(
            "A message will be sent to the customer's email address once the payment is successful"
          )}
        </p>
      </div>
    </div>
  );
};
